/**
 * Agent A3 — Disclosure Drafting + A3b Questionnaire Answering (V85-4-3).
 *
 * A3 takes system cards, evidence, and prior disclosure version, then
 * generates draft disclosure entries with per-field provenance.
 * Sets entry_review_states = 'draft'.
 * Uses harness with queue_target = 'ai_disclosure_versions'.
 *
 * A3b answers buyer questionnaires by citing released disclosure fields.
 * Each answer cites field_path + release_id. No answer without citation.
 *
 * Both run through the V85-4-0 agent harness.
 *
 * Auth model: N/A (pure agent function, workspace-scoped via harness).
 */
import { defineTemplate, field, referenceDerived } from '../egress/data-classes.js';
import { LlmRefusedError } from '../egress/gateway.js';
// ---- Agent definitions ------------------------------------------------------
export const A3_DEFINITION = {
    agent_id: 'A3',
    prompt_version: '2026.09.20',
    queue_target: 'ai_disclosure_versions',
    schema_version: '1.0',
};
export const A3B_DEFINITION = {
    agent_id: 'A3b',
    prompt_version: '2026.09.20',
    queue_target: 'findings',
    schema_version: '1.0',
};
export const A3_OUTPUT_SCHEMA = {
    type: 'object',
    required: ['system_id', 'summary', 'entry_review_state', 'fields'],
    properties: {
        system_id: { type: 'string' },
        summary: { type: 'string' },
        entry_review_state: { type: 'string' },
        fields: { type: 'array' },
        unchanged_fields: { type: 'array' },
        changed_fields: { type: 'array' },
    },
};
export const A3B_OUTPUT_SCHEMA = {
    type: 'object',
    required: ['system_id', 'summary', 'answers', 'unanswerable'],
    properties: {
        system_id: { type: 'string' },
        summary: { type: 'string' },
        answers: { type: 'array' },
        unanswerable: { type: 'array' },
    },
};
// ---- Prompt templates -------------------------------------------------------
const DISCLOSURE_DRAFT_PROMPT = `You are an AI disclosure drafting assistant. Given a system card, evidence, and optionally a prior disclosure version, draft disclosure entries.

## System
Name: {system_name}
System ID: {system_id}

## System Card
{system_card_section}

## Evidence ({evidence_count} items)
{evidence_section}

## Prior Disclosure Version
{prior_version_section}

## Instructions

Draft disclosure entries by mapping system card fields and evidence to the ADR-0023 disclosure schema:
- scope: products/services covered
- models: AI models used (name, version, provider, hosting_mode, purpose)
- providers: AI sub-processors (name, domain, data_sent, region)
- training_data_classes: training data categories (category, provenance, includes_customer_data)
- retention_and_training_use: data retention and training policies
- human_oversight: per-model oversight mode and override path
- incident: incident response contact, process, SLA
- frameworks_claimed: compliance framework claims
- evidence_refs: links to supporting evidence

Rules:
- Set entry_review_state to "draft" — never "published" or "approved"
- Every field must cite its source: system_card, evidence, prior_version, or inferred
- If a prior version exists, identify unchanged vs changed fields
- Do NOT fabricate data not present in the inputs
- Inferred fields must be clearly marked as source_type "inferred"

## Output format (JSON)

Return:
- summary: string
- entry_review_state: "draft"
- fields: array of { field_path, value, source_type, source_ref }
- unchanged_fields: string[] (field paths unchanged from prior)
- changed_fields: string[] (field paths that are new or modified)
`;
const QUESTIONNAIRE_PROMPT = `You are an AI disclosure questionnaire assistant. Answer buyer questions by citing specific fields from a released AI disclosure.

## System
Name: {system_name}
System ID: {system_id}

## Released Disclosure (release: {release_id})
{disclosure_section}

## Questions
{questions_section}

## Instructions

Answer each question by:
1. Finding the disclosure field that answers it
2. Citing the exact field_path and release_id
3. If no field answers the question, mark it as unanswerable with a reason

Rules:
- NEVER answer without a citation
- Each answer must reference a specific field_path from the disclosure
- Each answer must include the release_id
- If the disclosure doesn't contain relevant information, the question is unanswerable

## Output format (JSON)

Return:
- summary: string
- answers: array of { question_id, answer, cited_field_path, cited_release_id }
- unanswerable: array of { question_id, reason }
`;
/** ADR-0073: the A3 / A3b prompts as reviewed templates (system card, evidence and disclosure content are tenant data). */
export const A3_TEMPLATE = defineTemplate('agent.a3.disclosure_draft', '2026.09.20', DISCLOSURE_DRAFT_PROMPT, ['system_name', 'system_id', 'system_card_section', 'evidence_count', 'evidence_section', 'prior_version_section']);
export const A3B_TEMPLATE = defineTemplate('agent.a3b.disclosure_qa', '2026.09.20', QUESTIONNAIRE_PROMPT, ['system_name', 'system_id', 'release_id', 'disclosure_section', 'questions_section']);
// ---- Deterministic logic ----------------------------------------------------
/**
 * Extract disclosure fields deterministically from a system card.
 */
export function deterministicDisclosureDraft(input) {
    const fields = [];
    // Scope from system card
    if (input.system_card.purpose) {
        fields.push({
            field_path: 'scope.description',
            value: input.system_card.purpose,
            source_type: 'system_card',
            source_ref: `system_card.purpose`,
        });
    }
    // Models
    for (let i = 0; i < input.system_card.models.length; i++) {
        const model = input.system_card.models[i];
        fields.push({
            field_path: `models[${i}].name`,
            value: model.name,
            source_type: 'system_card',
            source_ref: `system_card.models[${i}].name`,
        });
        fields.push({
            field_path: `models[${i}].version`,
            value: model.version,
            source_type: 'system_card',
            source_ref: `system_card.models[${i}].version`,
        });
        fields.push({
            field_path: `models[${i}].provider`,
            value: model.provider,
            source_type: 'system_card',
            source_ref: `system_card.models[${i}].provider`,
        });
        fields.push({
            field_path: `models[${i}].hosting_mode`,
            value: model.hosting_mode,
            source_type: 'system_card',
            source_ref: `system_card.models[${i}].hosting_mode`,
        });
    }
    // Providers
    for (let i = 0; i < input.system_card.providers.length; i++) {
        const provider = input.system_card.providers[i];
        fields.push({
            field_path: `providers[${i}].name`,
            value: provider.name,
            source_type: 'system_card',
            source_ref: `system_card.providers[${i}].name`,
        });
        fields.push({
            field_path: `providers[${i}].domain`,
            value: provider.domain,
            source_type: 'system_card',
            source_ref: `system_card.providers[${i}].domain`,
        });
        fields.push({
            field_path: `providers[${i}].data_sent`,
            value: provider.data_sent,
            source_type: 'system_card',
            source_ref: `system_card.providers[${i}].data_sent`,
        });
        fields.push({
            field_path: `providers[${i}].region`,
            value: provider.region,
            source_type: 'system_card',
            source_ref: `system_card.providers[${i}].region`,
        });
    }
    // Evidence refs
    for (const ev of input.evidence) {
        fields.push({
            field_path: `evidence_refs`,
            value: { kind: 'evidence_artifact', ref: ev.evidence_id, label: ev.title },
            source_type: 'evidence',
            source_ref: `evidence:${ev.evidence_id}`,
        });
    }
    return fields;
}
/**
 * Compare draft fields against a prior version to find changes.
 */
export function computeFieldDelta(draftFields, priorContent) {
    if (!priorContent) {
        return { unchanged: [], changed: draftFields.map(f => f.field_path) };
    }
    const unchanged = [];
    const changed = [];
    for (const field of draftFields) {
        const priorValue = getNestedValue(priorContent, field.field_path);
        if (priorValue !== undefined && JSON.stringify(priorValue) === JSON.stringify(field.value)) {
            unchanged.push(field.field_path);
        }
        else {
            changed.push(field.field_path);
        }
    }
    return { unchanged, changed };
}
function getNestedValue(obj, path) {
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    let current = obj;
    for (const part of parts) {
        if (current === null || current === undefined || typeof current !== 'object') {
            return undefined;
        }
        current = current[part];
    }
    return current;
}
/**
 * Answer a questionnaire question by looking up the field in a disclosure.
 * Returns null if no matching field is found (unanswerable).
 */
export function deterministicQuestionnaireAnswer(question, disclosure) {
    // Try expected field paths first
    if (question.expected_field_paths) {
        for (const path of question.expected_field_paths) {
            const value = getNestedValue(disclosure.content, path);
            if (value !== undefined && value !== null) {
                return {
                    question_id: question.question_id,
                    answer: typeof value === 'string' ? value : JSON.stringify(value),
                    cited_field_path: path,
                    cited_release_id: disclosure.release_id,
                };
            }
        }
    }
    return null;
}
// ---- A3 Agent function ------------------------------------------------------
export const disclosureDraftingAgent = async (llm, input) => {
    const data = input.data;
    if (!data.system_id) {
        throw new Error('A3 disclosure drafting agent: system_id is required');
    }
    if (!data.system_card) {
        throw new Error('A3 disclosure drafting agent: system_card is required');
    }
    // Step 1: Deterministic extraction
    const deterministicFields = deterministicDisclosureDraft(data);
    const delta = computeFieldDelta(deterministicFields, data.prior_version?.content);
    // Step 2: LLM-enhanced drafting
    let llmFields = [];
    let summary = '';
    try {
        // ADR-0073: through the egress gateway; a refusal (no eligible provider for this
        // workspace's data) keeps the deterministic draft (parity agent_a3_disclosure).
        const result = await llm.run({
            task: 'agent.a3.disclosure_draft', template: A3_TEMPLATE,
            schemaHint: A3_OUTPUT_SCHEMA, options: { temperature: 0.1, maxTokens: 4096 },
            slots: {
                system_name: field('ai_system.description', data.system_name),
                system_id: field('ai_system.description', data.system_id),
                system_card_section: field('ai_system.card', JSON.stringify(data.system_card, null, 2)),
                evidence_count: referenceDerived('count', data.evidence.length),
                evidence_section: field('evidence.artifact_text', data.evidence.length > 0
                    ? data.evidence.map(e => `- [${e.evidence_id}] "${e.title}" (${e.evidence_type})${e.summary ? `: ${e.summary}` : ''}`).join('\n')
                    : 'No evidence items.'),
                prior_version_section: field('disclosure.content', data.prior_version
                    ? `Version ${data.prior_version.version_id} (published ${data.prior_version.published_at})`
                    : 'No prior version.'),
            },
        });
        if (result.status !== 'ok')
            throw new LlmRefusedError(result);
        summary = result.output.summary ?? '';
        // Validate LLM fields — ensure source references exist
        const validEvidenceIds = new Set(data.evidence.map(e => e.evidence_id));
        llmFields = (result.output.fields ?? []).filter(f => {
            if (f.source_type === 'evidence') {
                const refId = f.source_ref.replace('evidence:', '');
                return validEvidenceIds.has(refId);
            }
            return true;
        });
    }
    catch {
        summary = `Deterministic-only disclosure draft for ${data.system_name}: ` +
            `${deterministicFields.length} field(s). LLM unavailable.`;
    }
    // Merge: deterministic first, then unique LLM fields
    const deterministicPaths = new Set(deterministicFields.map(f => f.field_path));
    const uniqueLlmFields = llmFields.filter(f => !deterministicPaths.has(f.field_path));
    const allFields = [...deterministicFields, ...uniqueLlmFields];
    if (!summary) {
        summary = `Disclosure draft for ${data.system_name}: ` +
            `${allFields.length} field(s), ${delta.changed.length} changed, ${delta.unchanged.length} unchanged.`;
    }
    // Build provenance
    const provenance = {
        system_id: {
            source: 'input.system_id',
            span: data.system_id,
            confidence: 1.0,
        },
        summary: {
            source: 'agent.analysis',
            span: summary.slice(0, 200),
            confidence: 0.9,
        },
        entry_review_state: {
            source: 'policy.draft_only',
            span: 'draft',
            confidence: 1.0,
        },
        fields: {
            source: 'agent.analysis',
            span: `${allFields.length} fields (${deterministicFields.length} deterministic, ${uniqueLlmFields.length} LLM)`,
            confidence: 0.9,
        },
    };
    return {
        target: 'ai_disclosure_versions',
        proposal: {
            system_id: data.system_id,
            summary,
            entry_review_state: 'draft',
            fields: allFields,
            unchanged_fields: delta.unchanged,
            changed_fields: delta.changed,
        },
        provenance,
    };
};
// ---- A3b Agent function -----------------------------------------------------
export const questionnaireAnsweringAgent = async (llm, input) => {
    const data = input.data;
    if (!data.system_id) {
        throw new Error('A3b questionnaire agent: system_id is required');
    }
    if (!Array.isArray(data.questions)) {
        throw new Error('A3b questionnaire agent: questions array is required');
    }
    if (!data.released_disclosure) {
        throw new Error('A3b questionnaire agent: released_disclosure is required');
    }
    // Step 1: Deterministic answer matching
    const deterministicAnswers = [];
    const deterministicUnanswerable = [];
    for (const q of data.questions) {
        const answer = deterministicQuestionnaireAnswer(q, data.released_disclosure);
        if (answer) {
            deterministicAnswers.push(answer);
        }
        else {
            deterministicUnanswerable.push({
                question_id: q.question_id,
                reason: 'No matching field found in released disclosure.',
            });
        }
    }
    // Step 2: LLM-enhanced answer matching for unanswerable questions
    let llmAnswers = [];
    let llmUnanswerable = [];
    let summary = '';
    if (deterministicUnanswerable.length > 0) {
        try {
            // ADR-0073: through the egress gateway; a refusal keeps the deterministic answers
            // (parity agent_a3b_disclosure_qa).
            const result = await llm.run({
                task: 'agent.a3b.disclosure_qa', template: A3B_TEMPLATE,
                schemaHint: A3B_OUTPUT_SCHEMA, options: { temperature: 0.1, maxTokens: 4096 },
                slots: {
                    system_name: field('ai_system.description', data.system_name),
                    system_id: field('ai_system.description', data.system_id),
                    release_id: field('disclosure.content', data.released_disclosure.release_id),
                    disclosure_section: field('disclosure.content', JSON.stringify(data.released_disclosure.content, null, 2)),
                    questions_section: field('disclosure.buyer_questions', deterministicUnanswerable.map(u => {
                        const q = data.questions.find(q2 => q2.question_id === u.question_id);
                        return q ? `- [${q.question_id}] ${q.text}` : '';
                    }).filter(Boolean).join('\n')),
                },
            });
            if (result.status !== 'ok')
                throw new LlmRefusedError(result);
            summary = result.output.summary ?? '';
            // Validate: every answer must have a citation
            llmAnswers = (result.output.answers ?? []).filter(a => a.cited_field_path &&
                a.cited_release_id === data.released_disclosure.release_id &&
                getNestedValue(data.released_disclosure.content, a.cited_field_path) !== undefined);
            llmUnanswerable = result.output.unanswerable ?? [];
        }
        catch {
            summary = `Deterministic-only questionnaire answers for ${data.system_name}. LLM unavailable.`;
        }
    }
    // Merge answers
    const answeredQuestionIds = new Set(deterministicAnswers.map(a => a.question_id));
    const llmAnsweredIds = new Set(llmAnswers.map(a => a.question_id));
    const allAnswers = [
        ...deterministicAnswers,
        ...llmAnswers.filter(a => !answeredQuestionIds.has(a.question_id)),
    ];
    // Unanswerable: only those not answered by either path
    const allAnsweredIds = new Set(allAnswers.map(a => a.question_id));
    const allUnanswerable = [
        ...deterministicUnanswerable.filter(u => !llmAnsweredIds.has(u.question_id) && !allAnsweredIds.has(u.question_id)),
        ...llmUnanswerable.filter(u => !allAnsweredIds.has(u.question_id)),
    ];
    // Deduplicate unanswerable by question_id
    const seenUnanswerable = new Set();
    const dedupedUnanswerable = allUnanswerable.filter(u => {
        if (seenUnanswerable.has(u.question_id))
            return false;
        seenUnanswerable.add(u.question_id);
        return true;
    });
    if (!summary) {
        summary = `Questionnaire for ${data.system_name}: ` +
            `${allAnswers.length} answered, ${dedupedUnanswerable.length} unanswerable.`;
    }
    // Build provenance
    const provenance = {
        system_id: {
            source: 'input.system_id',
            span: data.system_id,
            confidence: 1.0,
        },
        summary: {
            source: 'agent.analysis',
            span: summary.slice(0, 200),
            confidence: 0.9,
        },
        answers: {
            source: 'agent.analysis',
            span: `${allAnswers.length} answers (${deterministicAnswers.length} deterministic)`,
            confidence: 0.9,
        },
        unanswerable: {
            source: 'agent.analysis',
            span: `${dedupedUnanswerable.length} unanswerable questions`,
            confidence: 0.9,
        },
    };
    // Per-answer provenance
    for (let i = 0; i < allAnswers.length; i++) {
        const a = allAnswers[i];
        provenance[`answer.${i}`] = {
            source: `disclosure:${a.cited_release_id}:${a.cited_field_path}`,
            span: a.answer.slice(0, 200),
            confidence: 1.0,
        };
    }
    return {
        target: 'findings',
        proposal: {
            system_id: data.system_id,
            summary,
            answers: allAnswers,
            unanswerable: dedupedUnanswerable,
        },
        provenance,
    };
};
//# sourceMappingURL=agent-disclosure-drafting-service.js.map